import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const STORAGE_KEY = "gpi_chat_messages";
const SESSION_KEY = "gpi_chat_session";

const GREETING = {
  role: "assistant",
  content:
    "Bonjour, je suis **Léa**, l'assistante du Cabinet GPI. Comment puis-je vous aider aujourd'hui ?\n\nVous pouvez me poser une question sur le syndic, l'expertise judiciaire ou nos services ASL.",
};

const QUICK_PROMPTS = [
  "Changer de syndic, comment ça marche ?",
  "Je dois faire constater un désordre dans la copropriété",
  "Qu'est-ce qu'une AG en visioconférence ?",
  "Combien coûte une expertise ?",
];

function formatContent(text) {
  // Minimal markdown: **bold**, line breaks
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [GREETING];
    } catch {
      return [GREETING];
    }
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem(SESSION_KEY) || (() => {
      const s = `gpi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, s);
      return s;
    })()
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;

    const userMsg = { role: "user", content: msg };
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);

    try {
      // Build history excluding the just-pushed message
      const history = messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .filter((m) => m !== GREETING);

      const { data } = await api.post("/chat", {
        session_id: sessionId.current,
        message: msg,
        history,
      });
      const reply = { role: "assistant", content: data.reply };
      setMessages((m) => [...m, reply]);
      if (!open) setHasUnread(true);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Désolée, je rencontre un souci technique. Vous pouvez nous joindre directement au **06 87 45 32 31** ou par email à **contact@cabinetgpi-immobilier.fr**.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessages([GREETING]);
    const s = `gpi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionId.current = s;
    localStorage.setItem(SESSION_KEY, s);
  };

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-toggle"
        aria-label="Ouvrir l'assistante GPI"
        className={`fixed bottom-6 right-6 z-[60] flex items-center gap-2.5 px-5 h-14 rounded-full shadow-2xl transition-all duration-300 ${
          open
            ? "bg-[#0F172A] text-white"
            : "bg-[#B8935A] hover:bg-[#0F172A] text-white"
        }`}
      >
        {open ? (
          <>
            <X size={20} />
            <span className="font-medium text-sm tracking-wide">Fermer</span>
          </>
        ) : (
          <>
            <MessageCircle size={20} />
            <span className="font-medium text-sm tracking-wide hidden sm:inline">
              Posez une question
            </span>
            {hasUnread && (
              <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            )}
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[400px] h-[600px] max-h-[80vh] bg-white border border-[#E2E8F0] shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-[#0F172A] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#B8935A] flex items-center justify-center shrink-0">
                <Sparkles size={18} />
              </div>
              <div>
                <div className="font-serif text-lg leading-tight">Léa</div>
                <div className="text-xs text-[#D4B584]">Assistante Cabinet GPI</div>
              </div>
            </div>
            <button
              onClick={reset}
              data-testid="chat-reset"
              title="Nouvelle conversation"
              className="text-[#94A3B8] hover:text-white text-xs underline underline-offset-2"
            >
              Effacer
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-[#F9F8F6]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#0F172A] text-white"
                      : "bg-white text-[#1E293B] border border-[#E2E8F0]"
                  }`}
                >
                  {formatContent(m.content)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#E2E8F0] px-4 py-3 flex gap-1.5">
                  <span className="w-2 h-2 bg-[#B8935A] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-[#B8935A] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-[#B8935A] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="space-y-2 pt-2">
                <div className="text-xs font-overline text-[#94A3B8]">Suggestions</div>
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p}
                    onClick={() => send(p)}
                    data-testid="chat-quick-prompt"
                    className="block w-full text-left px-3 py-2 text-sm bg-white border border-[#E2E8F0] hover:border-[#B8935A] hover:text-[#0F172A] text-[#475569] transition-colors"
                  >
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-[#E2E8F0] p-3 flex gap-2 bg-white"
          >
            <input
              ref={inputRef}
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Votre question..."
              disabled={loading}
              className="flex-1 px-4 py-2.5 border border-[#E2E8F0] bg-[#F9F8F6] focus:border-[#0F172A] focus:bg-white outline-none text-sm transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              data-testid="chat-send"
              disabled={loading || !input.trim()}
              className="px-4 bg-[#0F172A] text-white hover:bg-[#B8935A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Envoyer"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
